/* @flow */

import type { SearchResult } from "shop-state/types";

import React from "react";
import { Helmet } from "react-helmet-async";
import { search } from "@crossroads/shop-state/search";
import { useData, useSendMessage } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { SearchData } from "data";
import LoadingView from "components/LoadingView";
import { useLocation } from "react-router-dom";
import Breadcrumbs from "components/Breadcrumbs";
import { Wrapper, FilterableProductList } from "@crossroads/ui-components";
import ProductCard from "components/ProductCard";
import { useUi } from "helpers/ui";
import { PAGE_SIZE } from "effects/route";

import styles from "./styles.scss";

type SearchProps = {
  data: SearchResult,
  query: string,
  updating: boolean,
};

type WrapperProps = {
  query: string,
};

const SearchViewWrapper = ({ query }: WrapperProps) => {
  const search = useData(SearchData);

  if (search.state === "LOADED" || search.state === "UPDATING") {
    return (
      <SearchView
        data={search.data}
        query={query}
        updating={search.state === "UPDATING"}
      />
    );
  }

  return <LoadingView />;
};

const SearchView = ({ data, query, updating }: SearchProps) => {
  const t = useTranslate();
  const sendMessage = useSendMessage();
  const location = useLocation();
  const numPages = Math.ceil(data.totalCount / PAGE_SIZE);

  const load = location => sendMessage(search(query, location, true));

  if (data.items.length === 0) {
    return (
      <Wrapper className={styles.top}>
        <Breadcrumbs className={styles.breadcrumbs} current={t("SEARCH.RESULTS", { query })} />
        <p>{t("SEARCH.EMPTY")}</p>
      </Wrapper>
    );
  }

  return (
    <>
      <Helmet title={`${t("SEARCH.TITLE_PREFIX")}${query}`} />

      <Wrapper className={styles.top}>
        <Breadcrumbs className={styles.breadcrumbs} current={t("SEARCH.RESULTS", { query })} />

        <h1>
          <span>
            {data.totalCount === 1 && t("CATEGORY.PRODUCTS_IN_CATEGORY_TITLE_SINGULAR")}
            {data.totalCount > 1 && t("CATEGORY.PRODUCTS_IN_CATEGORY_TITLE_PLURAL", { itemsCount: data.totalCount })}
          </span>&nbsp;{t("SEARCH.RESULTS", { query })}
        </h1>
      </Wrapper>

      <FilterableProductList
        uiContext={useUi}
        updating={updating}
        productList={data}
        load={load}
        numPages={numPages}
        ProductCardComponent={ProductCard}
        breadcrumbLinks={[
          {
            pathname: location.pathname,
            search: location.search,
            title: t("SEARCH.RESULTS", { query }),
            hint: {
              name: t("SEARCH.RESULTS", { query }),
            },
          },
        ]}
        category={`${t("SEARCH.TITLE_PREFIX")}${query}`}
      />
    </>
  );
};

export default SearchViewWrapper;
